import React, {FC, useEffect, useState} from "react";
import {
   GlobalLayout,
   PanelLayout,
   StyledDiv,
   StyledOutlinedInput,
   StyledTypography
} from "../../providers/layout/LayoutProvider";
import {makeStyles} from "@mui/styles";
import {
   useConsumerSendMailMutation,
   useSpejderSportConsumerLookupMutation,
   useSpejderSportConsumerLookupResultQuery
} from "../../Queries";
import {isValidEmail} from "../../components/Validations";
import clsx from "clsx";
import {Box, Button, CircularProgress, darken, styled, Theme} from "@mui/material";
import {useRecoilValue, useSetRecoilState} from "recoil";
import {LocalizationsAtom} from "../../providers/localization/LocalizationProvider";
import {DropOffIcon, PackItIcon, TradeInIcon} from "../../svg/SvgIcons";
import {SpejderSportForm} from "./SpejderSportForm";
import {useNavigate} from "react-router-dom";
import {ReturnsPagePath} from "../restricted/returns/ReturnsPage";
import {Dialogs} from "../../Dialog";
import {LoginContext} from "../LoginContext";
import {LoggedIn} from "../../providers/access/AppContextProvider";
import {PubSub} from "../../utility/PubSub";

export const SpejderSportPage: FC<any> = () => {
   const [looking, setLooking] = useState<string[]>([]);
   const localizations = useRecoilValue(LocalizationsAtom);
   const classes = useStyles({
      leftImage: localizations.frontPageImage1 || "url('https://cdn.create2stay.com/brandstatic/ss_500x600_SECOND_HAND_280124_1.jpg')",
      rightImage: localizations.frontPageImage2 || "url('https://cdn.create2stay.com/brandstatic/ss_500x600_SECOND_HAND_300124_NY.jpg')",
   });
   const loggedIn = useRecoilValue(LoggedIn);

   const [lookup] = useSpejderSportConsumerLookupMutation()

   const onLookup = async (storeId: string, receiptNr: string) => {
      const {data} = await lookup({
         variables: {
            shopId: "6438fef8115629a57d746b9e",
            identity: receiptNr,
            parameters: storeId
         }
      });
      setLooking(data?.ConsumerRequestAccessFlow || [])
   }

   const anchorStyle: React.CSSProperties = {
      color: 'black',
      textDecoration: 'underline'
   }

   const onFailure = () => {
      Dialogs.info({
         title: 'Forkert Kvitteringsnummer eller butik',
         subtitle: 'Den valgte butik og det indtastede kvitteringsnummer matcher ikke.',
         okayText: 'OK',
      })
      setLooking([])
   }

   useEffect(() => {
      if (loggedIn && looking.length === 0) {
         PubSub.emit('logout');
      }
   }, [loggedIn, looking.length])

   return <Box>
      <StyledDiv type={"main"} className={classes.container}>
         <StyledDiv type={"main"} className={clsx(classes.image, classes.leftImage)}>
            &nbsp;
         </StyledDiv>
         <StyledDiv type={"contrast"} sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            flexGrow: 1,
            '@media only screen and (max-width: 1024px)': {
               paddingTop: '40px'
            },
            textAlign: 'center',
         }}>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: '10px', padding: '50px'}}>
               <Box sx={{width: '100%', textAlign: 'center'}}>
                  <StyledTypography variant={"h1"} type={"main"} style={{marginBottom: '15px'}} fontSize={100}>
                     {localizations.loginPageTitle}
                  </StyledTypography>
                  <StyledTypography variant={"body1"} type={"main"} style={{marginBottom: '30px'}}>
                     {localizations.loginPageDescription}<br/>
                  </StyledTypography>
                  <StyledTypography variant={"h2"} type={"main"} style={{marginBottom: '0px'}}>
                     {localizations.loginPageDescription2}<br/>
                  </StyledTypography>
               </Box>
               <Box sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '10px',
                  width: '100%',
                  flex: '1 1 0',
                  '@media only screen and (max-width: 1024px)': {
                     flexDirection: 'column'
                  },
               }}>
                  <Box className={classes.paper} sx={{
                     flex: '1 1 0',
                  }}>
                     <StyledTypography variant={"h2"} type={"main"} style={{marginBottom: '10px'}}>
                        {localizations.publicPageWholeSaleTitle}
                     </StyledTypography>
                     <StyledTypography variant={"body1"} type={"main"} style={{marginBottom: '20px'}}>
                        {localizations.publicPageWholeSaleDescription}
                     </StyledTypography>
                     <div className={classes.accessForm}>
                        <AccessForm/>
                     </div>
                  </Box>
                  <Box className={classes.paper} sx={{
                     flex: '1 1 0',
                  }}>
                     <StyledTypography variant={"h2"} type={"main"} style={{marginBottom: '10px'}}>
                        {localizations.publicPageAlternativeTitle}
                     </StyledTypography>
                     <StyledTypography variant={"body1"} type={"main"} style={{marginBottom: '20px'}}>
                        {localizations.publicPageAlternativeDescription}
                     </StyledTypography>
                     {looking.length > 0 && <SpejderSportLookup onFailure={onFailure} tokens={looking}/>}
                     <SpejderSportForm onLookup={onLookup} sx={looking.length !== 0 ? {
                        display: 'none'
                     } : {}}/>
                  </Box>
               </Box>
               <Box className={classes.paper}>
                  <StyledTypography variant={"h2"} type={"main"} style={{marginBottom: '10px'}}>
                     Ingen kvittering eller mail registeret?
                  </StyledTypography>
                  <StyledTypography variant={"body1"} type={"main"} style={{marginBottom: '10px'}}>
                     Tag dit grej under armen og aflever det i din lokale <a
                     href={"https://www.spejdersport.dk/StoreLocator?country=DK&distance=25&order=distance"}
                     rel={"noreferrer"} target={"_blank"} style={anchorStyle}>Spejder Sport butik</a>.
                     <br/>
                     Vi vurderer grejet i butikken, og du får dit gavekort med det samme.
                  </StyledTypography>
               </Box>
               <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  position: 'relative',
                  top: 20
               }}>
                  <a href={`https://www.spejdersport.dk/second-hand/`} style={{color: "blue"}}>Shop Second Hand-varer
                     her</a>
                  <a href={`https://www.spejdersport.dk/second-hand-faq/`} target="_blank" rel={"noreferrer"}
                     style={{color: "blue"}}>
                     Spørgsmål? Læs vores Second Hand FAQ</a>
               </Box>
            </Box>
         </StyledDiv>
         <StyledDiv type={"main"} className={clsx(classes.image, classes.rightImage)}>
            &nbsp;
         </StyledDiv>
      </StyledDiv>
      <SpejderSportEasyBox/>
   </Box>
}

type imageProps = {
   leftImage: string
   rightImage: string
}

const useStyles = makeStyles({
   backContainer: {
      marginTop: '20px'
   },
   paper: {
      borderRadius: '10px',
      backgroundColor: 'white',
      padding: '20px',
   },
   content: {
      maxWidth: '500px',
      textAlign: 'center',
      padding: '20px'
   },
   container: {
      display: 'flex',
      width: '100%',
      flexDirection: 'row',
      alignItems: 'top',
      padding: '0 40px 0 40px'
   },
   accessForm: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px'
   },
   image: {
      width: '400px',
      minHeight: '496px',
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      backgroundPosition: "top center",
      overflowX: 'hidden',
      overflowY: 'hidden',
   },
   leftImage: {
      backgroundImage: (props: imageProps) => props.leftImage
   },
   rightImage: {
      backgroundImage: (props: imageProps) => props.rightImage
   },

   '@media only screen and (max-width: 800px)': {
      container: {
         flexDirection: 'column',
         padding: '0'
      } as any,
      image: {
         width: '100%',
         maxHeight: '200px',
         minHeight: '300px',
         backgroundPosition: "center",
      }
   },
   '@media only screen and (max-width: 1024px)': {
      container: {
         padding: '0'
      } as any
   },
})

export const AccessForm = () => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const [email, setEmail] = React.useState<string>('');
   const [sent, setSent] = React.useState<boolean>(false);

   const classes = useAccessFormStyles();
   const [sendMail] = useConsumerSendMailMutation();

   const onChange = (event: any) => {
      setEmail(event.target.value);
      setSent(false);
   }

   const onSend = async () => {
      let shopId = window.shopId || window.brandId;
      if (!!email && isValidEmail(email)) {
         await sendMail({
            variables: {
               shopId: shopId,
               mail: email
            }
         });
         setEmail('');
         setSent(true);
      }
      //
   }

   return <>
      <form className={classes.lightBoxInputArea} onSubmit={async (e) => {
         e.preventDefault();
         await onSend();
      }}>
         <StyledOutlinedInput
            className={classes.input}
            id="email"
            type="text"
            value={email}
            onChange={onChange}
            placeholder={localizations.mainPageEnterEmail}
            aria-haspopup={false}
            autoComplete={"off"}
            endAdornment={
               <SpejderButton styletype={"contrast"} variant={"text"} className={classes.button} onClick={onSend}
                              disabled={!email}>
                  {localizations.mainPageSend}
               </SpejderButton>
            }
         />
      </form>
      {sent && <div className={classes.emailSent}>
         <StyledTypography variant={"body1"} className={classes.emailSentDivider}
                           type={"main"}>{localizations.mainPageMailSent}</StyledTypography>
         <StyledTypography variant={"body1"} type={"main"}>{localizations.mainPageMailSent2}</StyledTypography>
      </div>}
   </>
}

const useAccessFormStyles = makeStyles({
   input: {
      width: '100%',
      height: '45px',
      borderRadius: '10px',
      minWidth: '250px',
   },
   button: {
      height: '45px',
      borderRadius: '20px',
   },
   lightBoxInputArea: {
      display: 'flex',
      alignItems: 'center',
   },
   lightBoxMaxWidth: {
      maxWidth: '60%',
      margin: '0 auto'
   },
   emailSent: {
      marginTop: '20px',
      textAlign: 'center',
      padding: '20px',
   },
   emailSentDivider: {
      marginBottom: '15px',
   },
});

type StyledButtonProps = {
   styletype: 'main' | 'contrast'
}

export const SpejderButton = styled(Button)<StyledButtonProps>(({theme, variant, styletype}) => {
   const panelLayout = useRecoilValue(PanelLayout(styletype));
   const globalLayout = useRecoilValue(GlobalLayout);

   const button = panelLayout?.mainButton;

   return {
      color: '#56352C' || button?.textColor || undefined,
      '&:hover': {
         color: '#56352C' || button?.textColor || undefined,
         border: button?.border || undefined,
         backgroundColor: 'transparent'
      },
      '&:disabled': {
         color: button?.disabledTextColor || darken(button?.textColor || '#000000', 0.5) + ' !important',
      },
      fontSize: button?.fontSize || globalLayout.text?.fontSize || undefined,
      textTransform: 'none',
      borderRadius: '0',
      fontWeight: 400 || button?.fontWeight || globalLayout.text?.fontWeight || 'bold',
      fontFamily: button?.fontFamily || globalLayout.text?.fontFamily || undefined,
      border: button?.border || undefined,
      textDecoration: 'underline'
   }
});

const SpejderSportEasyBox = () => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const classes = useEasyStyles();
   return <div className={classes.container}>
      <div>
         <div className={classes.list}>
            <div className={classes.listItem}>
               <div className={classes.listAvatar}>
                  <TradeInIcon type={"contrast"}/>
               </div>
               <div className={classes.listText}>
                  <StyledTypography variant={"h2"} className={classes.text}>
                     {localizations.easyBoxSection1Title}
                  </StyledTypography>
                  <StyledTypography variant={"body1"}>
                     {localizations.easyBoxSection1Description}
                  </StyledTypography>
               </div>
            </div>
            <div className={classes.listItem}>
               <div className={classes.listAvatar}>
                  <PackItIcon type={"contrast"}/>
               </div>
               <div className={classes.listText}>
                  <StyledTypography variant={"h2"} className={classes.text}>
                     {localizations.easyBoxSection2Title}
                  </StyledTypography>
                  <StyledTypography variant={"body1"}>
                     {localizations.easyBoxSection2Description}
                  </StyledTypography>
               </div>
            </div>
            <div className={classes.listItem}>
               <div className={classes.listAvatar}>
                  <DropOffIcon type={"contrast"}/>
               </div>
               <div className={classes.listText}>
                  <StyledTypography variant={"h2"} className={classes.text}>
                     {localizations.easyBoxSection3Title}
                  </StyledTypography>
                  <StyledTypography variant={"body1"}>
                     {localizations.easyBoxSection3Description}
                  </StyledTypography>
               </div>
            </div>
         </div>
      </div>
   </div>
}

const useEasyStyles = makeStyles((theme: Theme) => ({
      container: {
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'center',
      },
      title: {
         marginTop: '40px'
      },
      text: {
         marginBottom: '15px'
      },
      list: {
         display: 'flex',
         flexDirection: 'row',
         justifyContent: 'center',
         marginTop: '40px',
      },
      listItem: {
         display: 'flex',
         flexDirection: 'column',
         alignItems: 'center',
         marginBottom: '20px',
         gap: '10px'
      },
      listAvatar: {},
      listText: {
         marginTop: '0',
         textAlign: 'center',
         maxWidth: '400px',
         padding: '0 20px 0 20px',
      },
      image: {
         width: '100px',
      },
      '@media only screen and (max-width: 800px)': {
         list: {
            flexDirection: 'column',
         },
         listItem: {
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            maxWidth: '600px',
            gap: '10px'
         },
      }
   }),
);

type SpejderSportLookupProps = {
   tokens: string[]
   onFailure: () => void
}

const SpejderSportLookup = ({tokens, onFailure}: SpejderSportLookupProps) => {
   const {data, stopPolling} = useSpejderSportConsumerLookupResultQuery({
      variables: {
         tokens
      },
      fetchPolicy: "no-cache",
      pollInterval: 1000
   });
   const setLoginContext = useSetRecoilState(LoginContext)
   const isEmptyOrUndefined = (val: string | undefined): boolean => val === '' || val === undefined;

   const navigate = useNavigate();
   useEffect(() => {
      for (const msg of data?.IntegrationMessage || []) {
         if (msg?.messageFailed) {
            stopPolling();
            onFailure()
         } else if (!isEmptyOrUndefined(msg?.result?.accessToken)) {
            setLoginContext({
               accessToken: msg?.result.accessToken,
            })
            navigate(ReturnsPagePath());
         }
      }
   }, [data, navigate, onFailure, stopPolling, setLoginContext])

   return <Box>
      <CircularProgress/>
   </Box>
}